import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertCircle, ArrowRight, CircleDot, ClipboardCheck, Clock3, Laptop, Monitor,
    Printer, RefreshCw, ShieldAlert, TicketCheck, UserRoundX,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { request } from '@/api/axios';
import { useAuth } from '@/auth/AuthContext';
import { CallDashboardSummary } from '@/types/call';
import {
    CALL_PRIORITY_LABELS, CALL_PRIORITY_STYLES, CALL_STATUS_LABELS, CALL_STATUS_STYLES,
    formatCallDate, formatCallProtocol,
} from '@/utils/callPresentation';

interface DashboardStats {
    numberOfComputers: number;
    numberOfNotebooks: number;
    numberOfMonitors: number;
    numberOfPrinters: number;
}

const emptyCallSummary: CallDashboardSummary = {
    totalActive: 0, newCalls: 0, inProgress: 0, waiting: 0, urgent: 0, unassigned: 0, recentCalls: [],
};

export default function Dashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [callSummary, setCallSummary] = useState<CallDashboardSummary>(emptyCallSummary);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const canCreateCalls = ['ADMIN', 'TECHNICAL_ADMIN', 'TECHNICAL'].includes(user?.role || '');

    useEffect(() => {
        let mounted = true;
        Promise.all([
            request<DashboardStats>({ url: '/dashboard/statistic', method: 'GET' }),
            request<CallDashboardSummary>({ url: '/dashboard/calls', method: 'GET' }),
        ]).then(([assetData, callData]) => {
            if (!mounted) return;
            setStats(assetData);
            setCallSummary(callData);
        }).catch(() => mounted && setError('Não foi possível carregar os indicadores da dashboard.'))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);

    const serviceCards = [
        { title: 'Ativos', value: callSummary.totalActive, icon: TicketCheck, color: 'text-blue-300', background: 'bg-blue-500/10 border-blue-500/20' },
        { title: 'Novos / triagem', value: callSummary.newCalls, icon: CircleDot, color: 'text-violet-300', background: 'bg-violet-500/10 border-violet-500/20' },
        { title: 'Em andamento', value: callSummary.inProgress, icon: ClipboardCheck, color: 'text-emerald-300', background: 'bg-emerald-500/10 border-emerald-500/20' },
        { title: 'Aguardando', value: callSummary.waiting, icon: Clock3, color: 'text-amber-300', background: 'bg-amber-500/10 border-amber-500/20' },
        { title: 'Urgentes', value: callSummary.urgent, icon: ShieldAlert, color: 'text-red-300', background: 'bg-red-500/10 border-red-500/20' },
        { title: 'Sem técnico', value: callSummary.unassigned, icon: UserRoundX, color: 'text-orange-300', background: 'bg-orange-500/10 border-orange-500/20' },
    ];

    const assetCards = [
        { title: 'Computadores', value: stats?.numberOfComputers ?? 0, icon: Monitor, color: 'text-blue-400' },
        { title: 'Notebooks', value: stats?.numberOfNotebooks ?? 0, icon: Laptop, color: 'text-emerald-400' },
        { title: 'Monitores', value: stats?.numberOfMonitors ?? 0, icon: Monitor, color: 'text-purple-400' },
        { title: 'Impressoras', value: stats?.numberOfPrinters ?? 0, icon: Printer, color: 'text-amber-400' },
    ];

    if (loading) {
        return <div className="page-content flex min-h-[420px] items-center justify-center gap-3 text-sm font-bold text-blue-300"><RefreshCw className="animate-spin" size={21} /> Preparando visão operacional...</div>;
    }
    if (error) {
        return <div className="page-content"><div className="feedback-error flex items-center gap-2"><AlertCircle size={18} /> {error}</div></div>;
    }

    return (
        <div className="page-content">
            <section>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div><p className="text-[9px] font-black uppercase tracking-[0.2em] text-blue-400">Atendimento em tempo real</p><h2 className="mt-1 text-lg font-black text-white">Situação dos chamados</h2></div>
                    <button type="button" onClick={() => navigate('/tickets')} className="ui-button-secondary self-start px-4 py-2.5 sm:self-auto">Ver todos <ArrowRight size={15} /></button>
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                    {serviceCards.map((card, index) => {
                        const Icon = card.icon;
                        return (
                            <motion.button key={card.title} type="button" onClick={() => navigate('/tickets')} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }} whileHover={{ y: -3 }} className="ui-card ui-card-hover p-4 text-left md:p-5">
                                <div className={`mb-5 inline-flex rounded-xl border p-2 ${card.background} ${card.color}`}><Icon size={18} /></div>
                                <strong className="block text-2xl font-black text-white">{card.value}</strong>
                                <span className="mt-1 block text-[9px] font-black uppercase tracking-wider text-slate-500">{card.title}</span>
                            </motion.button>
                        );
                    })}
                </div>
            </section>

            <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="ui-card p-0 md:p-0">
                <header className="flex items-center justify-between border-b border-white/5 px-5 py-4 md:px-6">
                    <div><h2 className="text-xs font-black uppercase tracking-wider text-white">Chamados recentes</h2><p className="mt-1 text-[10px] text-slate-600">Últimos protocolos registrados na secretaria ativa.</p></div>
                    {canCreateCalls && <button type="button" onClick={() => navigate('/tickets/new')} className="text-[10px] font-black uppercase tracking-wider text-blue-400 transition hover:text-blue-300">Novo chamado</button>}
                </header>
                {callSummary.recentCalls.length === 0 ? (
                    <div className="flex min-h-48 flex-col items-center justify-center p-8 text-center"><TicketCheck size={30} className="mb-3 text-slate-700" /><p className="text-xs font-bold text-slate-400">Nenhum chamado registrado ainda.</p>{canCreateCalls && <button type="button" onClick={() => navigate('/tickets/new')} className="mt-3 text-[10px] font-black uppercase tracking-wider text-blue-400">Registrar o primeiro</button>}</div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {callSummary.recentCalls.map(call => (
                            <button key={call.id} type="button" onClick={() => navigate(`/tickets/${call.id}`)} className="grid w-full gap-3 px-5 py-4 text-left transition hover:bg-blue-500/[0.04] md:grid-cols-[130px_minmax(0,1fr)_180px_180px_auto] md:items-center md:px-6">
                                <div><strong className="text-xs text-blue-300">{formatCallProtocol(call.id)}</strong><span className="mt-1 block text-[9px] text-slate-600">{formatCallDate(call.openingDate)}</span></div>
                                <div className="min-w-0"><strong className="block truncate text-xs text-slate-200">{call.applicantName || 'Solicitante não informado'}</strong><p className="mt-1 truncate text-[10px] text-slate-600">{call.description}</p></div>
                                <div className="min-w-0"><span className="block truncate text-[10px] text-slate-400">{call.categoryName || 'Sem categoria'}</span><span className="mt-1 block truncate text-[9px] text-slate-600">{call.responsibleTechnicianName || 'Sem técnico'}</span></div>
                                <div className="flex flex-wrap gap-1.5"><span className={`rounded-full border px-2 py-1 text-[8px] font-black uppercase ${CALL_STATUS_STYLES[call.status]}`}>{CALL_STATUS_LABELS[call.status]}</span><span className={`rounded-full border px-2 py-1 text-[8px] font-black uppercase ${CALL_PRIORITY_STYLES[call.priority]}`}>{CALL_PRIORITY_LABELS[call.priority]}</span></div>
                                <ArrowRight size={15} className="hidden text-slate-600 md:block" />
                            </button>
                        ))}
                    </div>
                )}
            </motion.section>

            <section>
                <div className="mb-4"><p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">Patrimônio</p><h2 className="mt-1 text-lg font-black text-white">Equipamentos cadastrados</h2></div>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {assetCards.map((card, index) => {
                        const Icon = card.icon;
                        return <motion.div key={card.title} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 + index * 0.04 }} className="ui-card flex items-center gap-4 p-4 md:p-5"><div className={`rounded-2xl border border-white/5 bg-[#0b0f1a] p-3 ${card.color}`}><Icon size={21} /></div><div><strong className="block text-2xl font-black text-white">{card.value}</strong><span className="text-[9px] font-black uppercase tracking-wider text-slate-600">{card.title}</span></div></motion.div>;
                    })}
                </div>
            </section>
        </div>
    );
}
